import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import {
  HolidayVillage as HolidayIcon,
  Star as StarIcon,
  School as SchoolIcon,
  Event as EventIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import ModalForms from '../../../../Shared/Modals/index.js';
import SearchableDropdown from '../../../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface HolidayDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (holidayType: string, reason: string) => void;
  existingHolidayType?: string;
  existingReason?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const HolidayDialog: React.FC<HolidayDialogProps> = ({
  open,
  onClose,
  onSave,
  existingHolidayType,
  existingReason,
}) => {
  const holidayOptions = [
    { value: 'weekly_off',  label: 'Weekly Off' },
    { value: 'government',  label: 'Government Holiday' },
    { value: 'school',      label: 'School Holiday' },
    { value: 'festival',    label: 'Festival' },
    { value: 'exam',        label: 'Exam/Assessment' },
    { value: 'emergency',   label: 'Emergency Closure' },
    { value: 'other',       label: 'Other' },
  ];

  const getInitialOption = () =>
    holidayOptions.find((opt) => opt.value === (existingHolidayType || 'weekly_off')) || holidayOptions[0];

  const [selectedHoliday, setSelectedHoliday] = useState(getInitialOption());
  const [reason, setReason] = useState(existingReason || '');

  const handleSave = () => {
    onSave(selectedHoliday?.value || 'weekly_off', reason);
  };

  // ── Modal Body ───────────────────────────────────────────────────────────────

  const modalBody = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SearchableDropdown
        options={holidayOptions}
        labelKey="label"
        valueKey="value"
        label="Holiday Type"
        value={selectedHoliday}
        onChange={(val) => setSelectedHoliday(val)}
      />

      <TextField
        fullWidth
        label="Reason/Description"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g., Independence Day, Diwali, Teacher's Day..."
        multiline
        rows={2}
        sx={{ '& .MuiInputBase-input': { fontSize: '0.9rem' } }}
      />

      <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
          Holiday Types:
        </Typography>
        <ul style={{ margin: '4px 0', paddingLeft: '20px', fontSize: '0.8rem' }}>
          <li><strong>Weekly Off:</strong> Regular weekly holiday (Sunday)</li>
          <li><strong>Government:</strong> National/State government declared holiday</li>
          <li><strong>School:</strong> School-specific holiday</li>
          <li><strong>Festival:</strong> Religious/cultural festival</li>
          <li><strong>Exam/Assessment:</strong> No regular classes due to exams</li>
          <li><strong>Emergency Closure:</strong> Unexpected closure</li>
        </ul>
      </Alert>
    </Box>
  );

  // ── Modal Footer / Header (same as before) ───────────────────────────────────

  const modalFooter = (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
      <Button onClick={onClose} size="medium" sx={{ fontSize: '0.85rem' }}>
        Cancel
      </Button>
      <Button
        onClick={handleSave}
        variant="contained"
        size="medium"
        sx={{ fontSize: '0.85rem' }}
      >
        Save Holiday
      </Button>
    </Box>
  );

  const modalHeader = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <HolidayIcon color="warning" sx={{ fontSize: 24 }} />
      Configure Holiday
    </Box>
  );

  return (
    <ModalForms
      show={open}
      handleClose={onClose}
      modalHeader={modalHeader}
      modalBody={modalBody}
      modalFooter={modalFooter}
      size="md"
    />
  );
};

export default HolidayDialog;